import React, { useRef, useEffect, useState } from "react";
import { Modal, Button, Slider, Spin } from "antd";
import { PlusOutlined, MinusOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useImageCropper } from "../../../hooks/useImageCropper";

interface MediaEditorProps {
  visible: boolean;
  file: File | null;
  onCancel: () => void;
  onConfirm: (croppedBlob: Blob) => void;
  aspectRatio?: number; // width / height, default 1 (square)
}

const EditorContainer = styled.div`
  position: relative;
  width: 100%;
  height: 500px;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  user-select: none;
`;

const ImageCanvas = styled.canvas`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const CropOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

const CropArea = styled.div<{ $x: number; $y: number; $width: number; $height: number; $offsetX: number; $offsetY: number }>`
  position: absolute;
  left: ${(props) => props.$offsetX + props.$x}px;
  top: ${(props) => props.$offsetY + props.$y}px;
  width: ${(props) => props.$width}px;
  height: ${(props) => props.$height}px;
  border: 2px solid var(--color-surface);
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.5);
  cursor: move;
  pointer-events: auto;

  &::before {
    content: "";
    position: absolute;
    top: 33.33%;
    left: 0;
    right: 0;
    height: 1px;
    background: rgba(255, 255, 255, 0.3);
  }

  &::after {
    content: "";
    position: absolute;
    top: 66.66%;
    left: 0;
    right: 0;
    height: 1px;
    background: rgba(255, 255, 255, 0.3);
  }
`;

const VerticalLine1 = styled.div`
  position: absolute;
  left: 33.33%;
  top: 0;
  bottom: 0;
  width: 1px;
  background: rgba(255, 255, 255, 0.3);
`;

const VerticalLine2 = styled.div`
  position: absolute;
  left: 66.66%;
  top: 0;
  bottom: 0;
  width: 1px;
  background: rgba(255, 255, 255, 0.3);
`;

const ResizeHandle = styled.div`
  position: absolute;
  bottom: -4px;
  right: -4px;
  width: 20px;
  height: 20px;
  background: var(--color-surface);
  border: 2px solid var(--color-info);
  border-radius: 50%;
  cursor: nwse-resize;
  pointer-events: auto;

  &:hover {
    background: var(--color-info);
  }
`;

const ControlsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 0;
`;

const ZoomControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 500px;
  background: #000;
`;

export const MediaEditor: React.FC<MediaEditorProps> = ({
  visible,
  file,
  onCancel,
  onConfirm,
  aspectRatio = 1,
}) => {
  const { t } = useTranslation();
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Update container width when container is mounted or modal becomes visible
  useEffect(() => {
    if (visible && containerRef.current) {
      // Use setTimeout to ensure DOM is fully rendered
      const timer = setTimeout(() => {
        if (containerRef.current) {
          setContainerWidth(containerRef.current.clientWidth);
        }
      }, 0);
      return () => clearTimeout(timer);
    } else if (!visible) {
      // Reset container width when modal closes
      setContainerWidth(0);
    }
  }, [visible]);

  // Update container width after image loads
  useEffect(() => {
    if (image && containerRef.current && containerWidth === 0) {
      setContainerWidth(containerRef.current.clientWidth);
    }
  }, [image, containerWidth]);

  const {
    cropArea,
    zoom,
    imageDimensions,
    imageOffset,
    handleMouseDown,
    handleTouchStart,
    handleZoomChange,
    getCroppedImage,
  } = useImageCropper({
    image,
    containerWidth,
    containerHeight: 500,
    aspectRatio,
  });

  // Load image when file changes
  useEffect(() => {
    if (!file) {
      setImage(null);
      return;
    }

    setLoading(true);
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      setImage(img);
      setLoading(false);
      URL.revokeObjectURL(url);
    };

    img.onerror = () => {
      setLoading(false);
      URL.revokeObjectURL(url);
    };

    img.src = url;

    // Cleanup function to revoke URL if component unmounts
    return () => {
      URL.revokeObjectURL(url);
      setImage(null);
    };
  }, [file]);

  // Draw image on canvas
  useEffect(() => {
    if (!image || !canvasRef.current || !imageDimensions.width) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = imageDimensions.width;
    canvas.height = imageDimensions.height;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, 0, 0, imageDimensions.width, imageDimensions.height);
  }, [image, imageDimensions]);

  const handleConfirm = async () => {
    setProcessing(true);
    try {
      const croppedBlob = await getCroppedImage();
      if (croppedBlob) {
        onConfirm(croppedBlob);
      }
    } catch (error) {
      console.error("Error cropping image:", error);
    } finally {
      setProcessing(false);
    }
  };

  const handleCancel = () => {
    setImage(null);
    onCancel();
  };

  return (
    <Modal
      open={visible}
      title={t("mediaEditor.title")}
      onCancel={handleCancel}
      width={800}
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={processing}>
          {t("mediaEditor.cancel")}
        </Button>,
        <Button
          key="confirm"
          type="primary"
          onClick={handleConfirm}
          loading={processing}
          disabled={!image}
        >
          {t("mediaEditor.cropAndSave")}
        </Button>,
      ]}
      centered
      destroyOnClose
    >
      {loading ? (
        <LoadingContainer>
          <Spin size="large" />
        </LoadingContainer>
      ) : (
        <>
          <EditorContainer ref={containerRef}>
            <ImageCanvas ref={canvasRef} />
            {image && imageDimensions.width > 0 && (
              <CropOverlay>
                <CropArea
                  $x={cropArea.x}
                  $y={cropArea.y}
                  $width={cropArea.width}
                  $height={cropArea.height}
                  $offsetX={imageOffset.x}
                  $offsetY={imageOffset.y}
                  onMouseDown={(e) => handleMouseDown(e, "drag")}
                  onTouchStart={(e) => handleTouchStart(e, "drag")}
                >
                  <VerticalLine1 />
                  <VerticalLine2 />
                  <ResizeHandle
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      handleMouseDown(e, "resize");
                    }}
                    onTouchStart={(e) => {
                      e.stopPropagation();
                      handleTouchStart(e, "resize");
                    }}
                  />
                </CropArea>
              </CropOverlay>
            )}
          </EditorContainer>

          <ControlsContainer>
            <ZoomControls>
              <Button
                icon={<MinusOutlined />}
                onClick={() => handleZoomChange(zoom - 0.1)}
                disabled={zoom <= 0.5}
              />
              <Slider
                min={0.5}
                max={3}
                step={0.1}
                value={zoom}
                onChange={handleZoomChange}
                style={{ flex: 1 }}
              />
              <Button
                icon={<PlusOutlined />}
                onClick={() => handleZoomChange(zoom + 0.1)}
                disabled={zoom >= 3}
              />
            </ZoomControls>
          </ControlsContainer>
        </>
      )}
    </Modal>
  );
};
